import * as THREE from 'three';
import { SceneNode } from '../../core/SceneNode';
import type { BridgePin } from '../../../embed/MapBridge';
import { createMapMarkerTube } from './MapMarkerGeometry';

/** A navigation decision is part of the walk, not an amenity. It therefore gets
 *  a camera-facing fork sign instead of a media card or category cube. */
export function isDecisionPin(pin: BridgePin): boolean {
    return pin.kind === 'poi' && pin.category === 'navigation' && pin.subcategory === 'decision';
}

function createDecisionTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('2D canvas unavailable for map decision marker');

    // Soft diamond = distinct from the physical amenity cubes and readable over
    // both the terrain photograph and route tubes without a black contour.
    context.save();
    context.translate(256, 256);
    context.rotate(Math.PI / 4);
    context.fillStyle = '#F3B83F';
    context.beginPath();
    context.roundRect(-176, -176, 352, 352, 56);
    context.fill();
    context.restore();

    context.strokeStyle = '#102840';
    context.fillStyle = '#102840';
    context.lineWidth = 42;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.beginPath();
    context.moveTo(256, 355);
    context.lineTo(256, 245);
    context.lineTo(166, 155);
    context.moveTo(256, 245);
    context.lineTo(346, 155);
    context.stroke();
    for (const [x, angle] of [[166, -Math.PI * 0.75], [346, -Math.PI * 0.25]] as const) {
        context.save();
        context.translate(x, 155);
        context.rotate(angle);
        context.beginPath();
        context.moveTo(0, 0);
        context.lineTo(-52, 25);
        context.lineTo(-52, -25);
        context.closePath();
        context.fill();
        context.restore();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
    return texture;
}

export class MapDecisionPinNode extends SceneNode {
    private readonly localBounds: THREE.Box3;

    constructor(pin: BridgePin, signSize: number, poleHeight: number) {
        super(`MapDecisionPin_${pin.id}`);
        const group = new THREE.Group();
        group.name = this.name;
        group.userData.sceneNode = this;
        group.userData.bridgePinId = pin.id;

        const texture = createDecisionTexture();
        const signMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: true,
            depthWrite: false,
            toneMapped: false,
        });
        const sign = new THREE.Sprite(signMaterial);
        sign.name = `DecisionSign_${pin.id}`;
        sign.scale.set(signSize, signSize, 1);
        sign.userData.bridgePinId = pin.id;
        group.add(sign);

        const pole = createMapMarkerTube(
            -signSize * 0.5,
            -signSize * 0.5 - poleHeight,
            signSize * 0.01,
        );
        pole.name = `DecisionPole_${pin.id}`;
        pole.userData.bridgePinId = pin.id;
        group.add(pole);

        this._mesh = group;
        this.localBounds = new THREE.Box3(
            new THREE.Vector3(-signSize * 0.5, -signSize * 0.5 - poleHeight, -signSize * 0.05),
            new THREE.Vector3(signSize * 0.5, signSize * 0.5, signSize * 0.05),
        );
    }

    protected calculateBoundingBox(): THREE.Box3 {
        return this.localBounds.clone();
    }

    protected override onDestroy(): void {
        this._mesh?.traverse((object: THREE.Object3D) => {
            const renderable = object as THREE.Mesh | THREE.Sprite;
            (renderable as THREE.Mesh).geometry?.dispose?.();
            const material = renderable.material;
            const materials = Array.isArray(material) ? material : material ? [material] : [];
            for (const item of materials) {
                (item as THREE.SpriteMaterial).map?.dispose();
                item.dispose();
            }
        });
        this._mesh?.removeFromParent();
        this._mesh = null;
        super.onDestroy();
    }
}
